import { Controller } from "@hotwired/stimulus";
import "./component.scss";

export default class extends Controller {
  static targets = ["copy", "fallback", "field", "status"];
  static values = { url: String, copiedText: String, failedText: String };

  connect() {
    this.statusTimeout = null;
  }

  disconnect() {
    this.clearStatusTimeout();
  }

  async copy(event) {
    event?.preventDefault();

    try {
      const writeText = navigator.clipboard?.writeText;
      if (typeof writeText !== "function") throw new Error("clipboard unavailable");

      await writeText.call(navigator.clipboard, this.urlValue);
      this.confirmCopy();
    } catch (_error) {
      if (this.copyUsingLegacyApi()) {
        this.confirmCopy();
      } else {
        this.offerManualFallback();
      }
    }
  }

  copyUsingLegacyApi() {
    const previousFocus = document.activeElement;
    const textArea = document.createElement("textarea");
    textArea.value = this.urlValue;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    this.element.appendChild(textArea);
    textArea.select();

    try {
      return document.execCommand("copy");
    } catch (_error) {
      return false;
    } finally {
      textArea.remove();
      if (previousFocus instanceof HTMLElement && previousFocus !== document.body && previousFocus.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    }
  }

  confirmCopy() {
    this.clearStatusTimeout();

    if (this.hasFallbackTarget && !this.fallbackTarget.hidden) {
      if (this.fallbackTarget.contains(document.activeElement) && this.hasCopyTarget) {
        this.copyTarget.focus({ preventScroll: true });
      }
      this.fallbackTarget.hidden = true;
    }

    this.element.dataset.copyState = "success";
    this.announce(this.copiedTextValue);
    this.statusTimeout = window.setTimeout(() => this.resetStatus(), 3000);
  }

  offerManualFallback() {
    this.clearStatusTimeout();

    if (this.hasFallbackTarget) this.fallbackTarget.hidden = false;
    this.element.dataset.copyState = "error";
    this.announce(this.failedTextValue);

    if (!this.hasFieldTarget) return;

    this.fieldTarget.focus({ preventScroll: true });
    this.fieldTarget.select();
  }

  announce(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message;
  }

  resetStatus() {
    if (this.hasStatusTarget) this.statusTarget.textContent = "";
    delete this.element.dataset.copyState;
    this.statusTimeout = null;
  }

  clearStatusTimeout() {
    if (!this.statusTimeout) return;

    window.clearTimeout(this.statusTimeout);
    this.statusTimeout = null;
  }
}
